import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Observable, switchMap } from 'rxjs';

import { FeedFileService } from '../services/feed-file.service';
import {
  FEED_FILE_MAX_BYTES,
  CreateFeedFileRequest,
  FeedFile,
  FeedFileSourceConfig,
  FilesCapability,
  FileSourceType,
  UpdateFeedFileRequest,
  UrlAuthType,
  UrlSourceConfig,
} from '../models/provider.model';
import { ApiResponse } from '../../api-response';
import { Modal } from '../../../shared/components/modal/modal';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { Select, SelectOption } from '../../../shared/components/forms/select';
import { SegmentedControl } from '../../../shared/components/forms/segmented-control';
import { ToastService } from '../../../shared/services/toast-service';
import { LanguageService } from '../../../core/i18n/language.service';
import { formatBytes } from '../../../shared/utils/format-bytes';
import { acceptFor, declaredRoles, roleDeclared } from '../util/file-rules.util';
import { roleLabel } from '../util/descriptor-text';
import {
  FeedFileMetadataRow,
  URL_AUTH_TYPES,
  URL_DEFAULT_INTERVAL,
  URL_INTERVAL_PRESETS,
  apiErrorMessage,
  authNeedsSecret,
  buildFileMetadata,
  hasInvalidMetadata,
  isHttpUrl,
  isPlainFilename,
  metadataRowsFrom,
  statusErrorKey,
} from '../util/feed-file-upload';

type DialogField = 'filename' | 'role' | 'url' | 'username' | 'headerName' | 'paramName' | 'secret' | 'metadata' | 'content';

/**
 * Add / edit dialog for one feed file: filename (create only — fixed after),
 * role, metadata rows, the source (Upload = MANUAL, or a watched URL) and the
 * URL form — url, check interval preset, auth type with its username / header
 * / param name, and the write-only secret.
 *
 * Secret rules (core): never pre-filled; a stored one shows as "set" with a
 * Replace action. Omitting `secrets` keeps it, `{ URL: { secret } }` replaces
 * it, `{ URL: {} }` clears it (sent when the auth type no longer needs one).
 * `sourceConfig` replaces the stored settings as a whole, so switching to
 * MANUAL sends the file's existing URL settings back to keep them for later.
 *
 * On create with MANUAL, a file can be picked to upload right after. Saves
 * itself and emits the resulting file; core's error message is shown inline.
 */
@Component({
  selector: 'app-feed-file-dialog',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, Modal, FormField, TextInput, Select, SegmentedControl],
  templateUrl: './feed-file-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedFileDialogComponent implements OnInit {
  private readonly feedFileService = inject(FeedFileService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly feedId = input.required<string>();
  /** The file to edit; null = create. */
  readonly file = input<FeedFile | null>(null);
  /** Prefilled filename on create (e.g. GTFS's `gtfs.zip`). */
  readonly defaultFilename = input('');
  /** Source preselected on create. */
  readonly initialSource = input<FileSourceType>('MANUAL');
  /** `accept` for the optional content picker on create, when the provider's descriptor doesn't
   *  list content types for the chosen role. */
  readonly accept = input('');
  /** The provider's file rules (descriptor `capabilities.files`); null = none known. */
  readonly filesCapability = input<FilesCapability | null>(null);
  /** For role label translation keys. */
  readonly providerType = input<string | null>(null);

  readonly saved = output<FeedFile>();
  readonly closed = output<void>();

  readonly open = signal(true);
  readonly isSaving = signal(false);
  readonly error = signal<string | null>(null);

  readonly filename = signal('');
  readonly role = signal('');
  readonly metadataRows = signal<FeedFileMetadataRow[]>([]);
  readonly sourceType = signal<FileSourceType>('MANUAL');
  readonly url = signal('');
  readonly interval = signal<string>(URL_DEFAULT_INTERVAL);
  readonly authType = signal<UrlAuthType>('NONE');
  readonly username = signal('');
  readonly headerName = signal('');
  readonly paramName = signal('');
  readonly secret = signal('');
  /** Editing a stored secret: true once the user chose to replace it. */
  readonly replacingSecret = signal(false);
  /** Optional content to upload right after create (MANUAL only). */
  readonly contentFile = signal<File | null>(null);

  readonly maxBytes = FEED_FILE_MAX_BYTES;
  readonly formatBytes = formatBytes;

  readonly isCreate = computed(() => !this.file());
  /** Declared roles turn the role field into a required select. */
  readonly rolesDeclared = computed(() => declaredRoles(this.filesCapability()).length > 0);
  readonly roleOptions = computed<SelectOption[]>(() => {
    this.languageService.current();
    return declaredRoles(this.filesCapability()).map((role) => {
      const label = roleLabel(this.transloco, this.providerType(), role.name);
      return {
        value: role.name,
        label: role.required ? this.transloco.translate('providers.feedFiles.dialog.roleRequiredOption', { role: label }) : label,
      };
    });
  });
  /** The content picker's `accept`: the chosen role's content types, else the `accept` input. */
  readonly pickerAccept = computed(() => acceptFor(this.filesCapability(), this.role().trim() || null, this.accept()));
  readonly secretStored = computed(() => !!this.file()?.secretsSet?.['URL']?.includes('secret'));
  readonly needsSecret = computed(() => this.sourceType() === 'URL' && authNeedsSecret(this.authType()));
  /** The secret field is editable (empty, never pre-filled) unless a stored one is kept. */
  readonly secretEditable = computed(() => !this.secretStored() || this.replacingSecret());
  /** The file's stored URL settings, if any — kept when the dialog saves MANUAL. */
  private readonly storedUrlConfig = computed(() => this.file()?.sourceConfig?.URL ?? null);

  readonly sourceSegments = computed(() => {
    this.languageService.current();
    return [
      { value: 'MANUAL', label: this.transloco.translate('providers.feedFiles.source.MANUAL') },
      { value: 'URL', label: this.transloco.translate('providers.feedFiles.source.URL') },
    ];
  });

  readonly intervalOptions = computed<SelectOption[]>(() => {
    this.languageService.current();
    const options: SelectOption[] = URL_INTERVAL_PRESETS.map((iso) => ({
      value: iso,
      label: this.transloco.translate(`providers.feedFiles.intervals.${iso}`),
    }));
    // Keep a stored non-preset interval selectable instead of silently changing it.
    const current = this.interval();
    if (!(URL_INTERVAL_PRESETS as readonly string[]).includes(current)) options.push({ value: current, label: current });
    return options;
  });

  readonly authOptions = computed<SelectOption[]>(() => {
    this.languageService.current();
    return URL_AUTH_TYPES.map((type) => ({ value: type, label: this.transloco.translate(`providers.feedFiles.auth.${type}`) }));
  });

  /** Field problems as translation keys under providers.feedFiles.dialog.errors. */
  readonly errors = computed(() => {
    const e: Partial<Record<DialogField, string>> = {};
    if (this.isCreate() && !isPlainFilename(this.filename())) e.filename = 'filename';
    if (this.rolesDeclared()) {
      const role = this.role().trim();
      if (!role) e.role = 'roleRequired';
      else if (!roleDeclared(this.filesCapability(), role)) e.role = 'roleUndeclared';
    }
    if (this.sourceType() === 'URL') {
      if (!isHttpUrl(this.url())) e.url = 'url';
      const type = this.authType();
      if (type === 'BASIC' && !this.username().trim()) e.username = 'required';
      if (type === 'HEADER' && !this.headerName().trim()) e.headerName = 'required';
      if (type === 'QUERY' && !this.paramName().trim()) e.paramName = 'required';
      if (this.needsSecret() && this.secretEditable() && !this.secret()) e.secret = 'required';
    }
    if (hasInvalidMetadata(this.metadataRows())) e.metadata = 'metadata';
    const content = this.contentFile();
    if (content && content.size > FEED_FILE_MAX_BYTES) e.content = 'tooLarge';
    return e;
  });

  readonly isValid = computed(() => Object.keys(this.errors()).length === 0);

  ngOnInit(): void {
    const f = this.file();
    if (!f) {
      this.filename.set(this.defaultFilename());
      this.sourceType.set(this.initialSource());
      return;
    }
    this.filename.set(f.filename);
    this.role.set(f.role ?? '');
    this.metadataRows.set(metadataRowsFrom(f.metadata));
    this.sourceType.set(f.sourceType);
    const url = f.sourceConfig?.URL;
    if (url) {
      this.url.set(url.url ?? '');
      this.interval.set(url.interval || URL_DEFAULT_INTERVAL);
      this.authType.set(url.auth?.type ?? 'NONE');
      this.username.set(url.auth?.username ?? '');
      this.headerName.set(url.auth?.headerName ?? '');
      this.paramName.set(url.auth?.paramName ?? '');
    }
  }

  setSource(value: string): void {
    this.sourceType.set(value === 'URL' ? 'URL' : 'MANUAL');
  }

  setAuthType(value: UrlAuthType): void {
    this.authType.set(value);
  }

  onContentPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.contentFile.set(input.files?.[0] ?? null);
    input.value = '';
  }

  addMetadataRow(): void {
    this.metadataRows.update((rows) => [...rows, { key: '', value: '' }]);
  }

  removeMetadataRow(index: number): void {
    this.metadataRows.update((rows) => rows.filter((_, i) => i !== index));
  }

  setMetadata(index: number, field: keyof FeedFileMetadataRow, value: string): void {
    this.metadataRows.update((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  /** Field error text, or null. */
  fieldError(field: DialogField): string | null {
    const key = this.errors()[field];
    return key ? this.transloco.translate(`providers.feedFiles.dialog.errors.${key}`, { max: formatBytes(FEED_FILE_MAX_BYTES) }) : null;
  }

  close(): void {
    if (this.isSaving()) return;
    this.open.set(false);
    this.closed.emit();
  }

  save(): void {
    if (!this.isValid() || this.isSaving()) return;
    this.isSaving.set(true);
    this.error.set(null);

    const f = this.file();
    const request$: Observable<ApiResponse<FeedFile>> = f
      ? this.feedFileService.update(this.feedId(), f.id, this.buildUpdate())
      : this.feedFileService.create(this.feedId(), this.buildCreate());

    const content = !f && this.sourceType() === 'MANUAL' ? this.contentFile() : null;
    request$
      .pipe(
        // Upload the picked content right after create; a failed upload still
        // leaves the (empty) file created, so it's reported but not fatal.
        switchMap((res) => (content ? this.uploadAfterCreate(res.data, content) : [res.data])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (saved) => {
          this.isSaving.set(false);
          this.toastService.show(this.transloco.translate(f ? 'providers.feedFiles.toast.updated' : 'providers.feedFiles.toast.created'), 'success');
          this.saved.emit(saved);
          this.open.set(false);
          this.closed.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          this.error.set(apiErrorMessage(err) ?? this.transloco.translate<string>(`providers.feedFiles.errors.${statusErrorKey(err.status)}`));
        },
      });
  }

  private uploadAfterCreate(created: FeedFile, content: File): Observable<FeedFile> {
    return new Observable<FeedFile>((subscriber) => {
      const sub = this.feedFileService.uploadContent(this.feedId(), created.id, content).subscribe({
        next: (res) => {
          subscriber.next(res.data);
          subscriber.complete();
        },
        error: (err: HttpErrorResponse) => {
          const message = apiErrorMessage(err) ?? this.transloco.translate<string>(`providers.feedFiles.errors.${statusErrorKey(err.status)}`);
          this.toastService.show(this.transloco.translate('providers.feedFiles.toast.uploadAfterCreateFailed', { message }), 'error');
          subscriber.next(created);
          subscriber.complete();
        },
      });
      return () => sub.unsubscribe();
    });
  }

  private buildUrlConfig(): UrlSourceConfig {
    const type = this.authType();
    const config: UrlSourceConfig = { url: this.url().trim(), interval: this.interval() };
    if (type !== 'NONE') {
      config.auth = { type };
      if (type === 'BASIC') config.auth.username = this.username().trim();
      if (type === 'HEADER') config.auth.headerName = this.headerName().trim();
      if (type === 'QUERY') config.auth.paramName = this.paramName().trim();
    }
    return config;
  }

  /** URL active: the form's settings. MANUAL: the stored URL settings sent
   *  back unchanged, so switching back later needs no re-entry. */
  private buildSourceConfig(): FeedFileSourceConfig | undefined {
    if (this.sourceType() === 'URL') return { URL: this.buildUrlConfig() };
    const stored = this.storedUrlConfig();
    return stored ? { URL: stored } : undefined;
  }

  private buildCreate(): CreateFeedFileRequest {
    const req: CreateFeedFileRequest = {
      filename: this.filename().trim(),
      sourceType: this.sourceType(),
      metadata: buildFileMetadata(this.metadataRows()),
    };
    if (this.role().trim()) req.role = this.role().trim();
    const sourceConfig = this.buildSourceConfig();
    if (sourceConfig) req.sourceConfig = sourceConfig;
    if (this.needsSecret()) req.secrets = { URL: { secret: this.secret() } };
    return req;
  }

  private buildUpdate(): UpdateFeedFileRequest {
    const req: UpdateFeedFileRequest = {
      role: this.role().trim(),
      metadata: buildFileMetadata(this.metadataRows()),
      sourceType: this.sourceType(),
    };
    const sourceConfig = this.buildSourceConfig();
    if (sourceConfig) req.sourceConfig = sourceConfig;
    if (this.sourceType() === 'URL') {
      if (this.needsSecret() && this.secretEditable() && this.secret()) {
        req.secrets = { URL: { secret: this.secret() } };
      } else if (!authNeedsSecret(this.authType()) && this.secretStored()) {
        // Auth no longer uses a secret — don't keep a stale one around.
        req.secrets = { URL: {} };
      }
    }
    return req;
  }
}
